import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (iso) => iso ? new Date(iso).toLocaleString() : '—';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

function statusBadge(status) {
  const map = {
    COMPLETED: 'bg-green-100 text-green-800',
    PROCESSED: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
    SCHEDULED: 'bg-purple-100 text-purple-800',
    BELOW_BAR: 'bg-red-100 text-red-700',
    MEETS_BAR: 'bg-green-100 text-green-700',
    EXCEEDS_BAR: 'bg-emerald-100 text-emerald-700',
    BORDERLINE: 'bg-yellow-100 text-yellow-700',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-600',
  };
  const val = (status || '').toUpperCase().replace(/ /g, '_');
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${map[val] || 'bg-gray-100 text-gray-600'}`}>
      {status || '—'}
    </span>
  );
}

// ── Section card ────────────────────────────────────────────────────────────
function Section({ title, icon, children, count }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-5">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        </div>
        {count !== undefined && (
          <span className="text-xs text-gray-400 font-medium">{count} records</span>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── Info row ────────────────────────────────────────────────────────────────
function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-400 font-medium w-40 flex-shrink-0">{label}</span>
      <span className={`text-xs text-gray-800 text-right ${mono ? 'font-mono' : ''}`}>
        {value || '—'}
      </span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════════════
export default function SupportDashboard() {
  const [token]        = useState(() => localStorage.getItem('token'));
  const [emailInput,   setEmailInput]   = useState('');
  const [loading,      setLoading]      = useState(false);
  const [errorMsg,     setErrorMsg]     = useState('');
  const [customerData, setCustomerData] = useState(null);

  const [errorsLoading, setErrorsLoading] = useState(false);
  const [platformErrors, setPlatformErrors] = useState(null);
  const [errHours, setErrHours] = useState(24);

  const [activeTab, setActiveTab] = useState('overview');

  // ── Customer lookup ────────────────────────────────────────────────────
  async function handleLookup(e) {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setLoading(true);
    setErrorMsg('');
    setCustomerData(null);
    setActiveTab('overview');
    try {
      const res = await fetch(
        `${API_BASE}/api/support/lookup?email=${encodeURIComponent(emailInput.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Lookup failed');
      setCustomerData(json.data);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Platform errors ────────────────────────────────────────────────────
  async function loadPlatformErrors() {
    setErrorsLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/support/errors?hours=${errHours}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Failed to load errors');
      setPlatformErrors(json);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setErrorsLoading(false);
    }
  }

  // ── Tabs ───────────────────────────────────────────────────────────────
  const tabs = [
    { key: 'overview',      label: '👤 Overview' },
    { key: 'interviews',    label: '💻 Interviews',   count: customerData?.recent_interviews?.length },
    { key: 'meetings',      label: '📞 Meetings',     count: customerData?.recent_meetings?.length },
    { key: 'requirements',  label: '📋 Requirements', count: customerData?.recent_requirements?.length },
    { key: 'errors',        label: '⚠️ Error Log',    count: customerData?.recent_errors?.length },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Top bar ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-sm">Q</div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Quali5d Support Portal</h1>
              <p className="text-xs text-gray-400">Internal use only — do not share</p>
            </div>
          </div>
          {/* Platform errors quick access */}
          <button
            onClick={() => { setPlatformErrors(null); loadPlatformErrors(); }}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold rounded-xl transition-all border border-red-200"
          >
            🔴 Platform Errors
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* ── Search box ─────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
          <h2 className="text-sm font-bold text-gray-700 mb-4">🔍 Customer Lookup</h2>
          <form onSubmit={handleLookup} className="flex gap-3">
            <input
              type="email"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder="Enter customer email address..."
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 bg-gray-50"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-all"
            >
              {loading ? 'Looking up...' : 'Look Up'}
            </button>
          </form>
          {errorMsg && (
            <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {errorMsg}
            </div>
          )}
        </div>

        {/* ── Platform errors panel ──────────────────────────────────── */}
        {platformErrors && (
          <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-bold text-gray-800">🔴 Platform Errors — Last {errHours}h</h2>
                <p className="text-xs text-gray-400 mt-0.5">{platformErrors.error_count} error{platformErrors.error_count !== 1 ? 's' : ''} found</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={errHours}
                  onChange={e => setErrHours(Number(e.target.value))}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50"
                >
                  {[1,6,24,48,72,168].map(h => (
                    <option key={h} value={h}>{h === 168 ? '7 days' : `${h}h`}</option>
                  ))}
                </select>
                <button onClick={loadPlatformErrors} disabled={errorsLoading}
                  className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium">
                  {errorsLoading ? 'Loading...' : 'Refresh'}
                </button>
                <button onClick={() => setPlatformErrors(null)}
                  className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium text-gray-500">
                  Close
                </button>
              </div>
            </div>
            {platformErrors.data.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No errors in this window 🎉</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {platformErrors.data.map((e, i) => (
                  <ErrorCard key={i} entry={e} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Customer context ───────────────────────────────────────── */}
        {customerData && (
          <>
            {/* Customer header bar */}
            <div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-5 mb-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-lg">
                  {(customerData.user.full_name || customerData.user.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">{customerData.user.full_name || '—'}</h2>
                  <p className="text-sm text-gray-400">{customerData.user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {statusBadge(customerData.user.is_active ? 'active' : 'inactive')}
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {customerData.user.role}
                </span>
                {customerData.company && (
                  <span className="text-xs font-semibold text-purple-700 bg-purple-50 px-3 py-1 rounded-full border border-purple-200">
                    {customerData.company.subscription_plan?.toUpperCase()} Plan
                  </span>
                )}
              </div>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 mb-5 bg-white rounded-xl border border-gray-100 shadow-sm p-1">
              {tabs.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    activeTab === t.key
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {t.label}
                  {t.count !== undefined && t.count > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      activeTab === t.key ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>{t.count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Overview tab ─────────────────────────────────────── */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Section title="User Details" icon="👤">
                  <InfoRow label="Full Name"   value={customerData.user.full_name} />
                  <InfoRow label="Email"       value={customerData.user.email} />
                  <InfoRow label="Role"        value={customerData.user.role} />
                  <InfoRow label="Status"      value={customerData.user.is_active ? 'Active' : 'Inactive'} />
                  <InfoRow label="Joined"      value={fmtDate(customerData.user.created_at)} />
                  <InfoRow label="Last Login"  value={fmt(customerData.user.last_login)} />
                  <InfoRow label="User ID"     value={`#${customerData.user.id}`} mono />
                </Section>

                {customerData.company && (
                  <Section title="Company Details" icon="🏢">
                    <InfoRow label="Company Name"  value={customerData.company.company_name} />
                    <InfoRow label="Company ID"    value={customerData.company.company_id} mono />
                    <InfoRow label="Plan"          value={customerData.company.subscription_plan} />
                    <InfoRow label="Status"        value={customerData.company.is_active ? 'Active' : 'Inactive'} />
                    <InfoRow label="Usage"
                      value={`${customerData.company.requirements_used} / ${customerData.company.monthly_limit}`} />
                  </Section>
                )}

                <Section title="Activity Summary" icon="📊">
                  <InfoRow label="Total Interviews"   value={customerData.recent_interviews.length} />
                  <InfoRow label="Total Meetings"     value={customerData.recent_meetings.length} />
                  <InfoRow label="Total Requirements" value={customerData.recent_requirements.length} />
                  <InfoRow label="Recent Errors"      value={customerData.recent_errors.length} />
                </Section>

                {customerData.recent_errors.length > 0 && (
                  <Section title="Latest Error" icon="🔴">
                    <ErrorCard entry={customerData.recent_errors[0]} />
                  </Section>
                )}
              </div>
            )}

            {/* ── Interviews tab ───────────────────────────────────── */}
            {activeTab === 'interviews' && (
              <Section title="Recent Interviews" icon="💻" count={customerData.recent_interviews.length}>
                {customerData.recent_interviews.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No interviews found</p>
                ) : (
                  <div className="space-y-3">
                    {customerData.recent_interviews.map(i => (
                      <div key={i.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{i.candidate_name}</p>
                          <p className="text-xs text-gray-400">{i.candidate_email} · {i.role_type}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{fmt(i.scheduled_at)}</p>
                        </div>
                        <div className="text-right space-y-1">
                          {statusBadge(i.status)}
                          {i.recommendation && statusBadge(i.recommendation)}
                          {i.overall_score != null && (
                            <p className="text-xs font-bold text-purple-700">{i.overall_score}/400</p>
                          )}
                          <p className="text-xs text-gray-300 font-mono">#{i.id}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* ── Meetings tab ─────────────────────────────────────── */}
            {activeTab === 'meetings' && (
              <Section title="Recent Meetings" icon="📞" count={customerData.recent_meetings.length}>
                {customerData.recent_meetings.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No meetings found</p>
                ) : (
                  <div className="space-y-3">
                    {customerData.recent_meetings.map(m => (
                      <div key={m.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{m.title}</p>
                          <p className="text-xs text-gray-400">{fmt(m.scheduled)}</p>
                        </div>
                        <div className="text-right">
                          {statusBadge(m.status)}
                          <p className="text-xs text-gray-300 font-mono mt-1">#{m.id}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* ── Requirements tab ─────────────────────────────────── */}
            {activeTab === 'requirements' && (
              <Section title="Recent Requirements" icon="📋" count={customerData.recent_requirements.length}>
                {customerData.recent_requirements.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No requirements found</p>
                ) : (
                  <div className="space-y-3">
                    {customerData.recent_requirements.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{r.title}</p>
                          <p className="text-xs text-gray-400">{fmtDate(r.created_at)}</p>
                        </div>
                        <div className="text-right">
                          {r.status && statusBadge(r.status)}
                          <p className="text-xs text-gray-300 font-mono mt-1">#{r.id}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* ── Error Log tab ─────────────────────────────────────── */}
            {activeTab === 'errors' && (
              <Section title="Recent Errors for this User" icon="⚠️" count={customerData.recent_errors.length}>
                {customerData.recent_errors.length === 0 ? (
                  <p className="text-sm text-green-600 text-center py-6 font-medium">
                    ✓ No errors found for this user
                  </p>
                ) : (
                  <div className="space-y-3">
                    {customerData.recent_errors.map((e, i) => (
                      <ErrorCard key={i} entry={e} />
                    ))}
                  </div>
                )}
              </Section>
            )}
          </>
        )}

        {/* ── Empty state ────────────────────────────────────────────── */}
        {!customerData && !loading && !platformErrors && (
          <div className="text-center py-20 text-gray-300">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-base font-semibold text-gray-400">Enter a customer email above to get started</p>
            <p className="text-sm text-gray-300 mt-1">
              Or click <span className="text-red-400 font-semibold">Platform Errors</span> to see recent errors across all users
            </p>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Error card sub-component ─────────────────────────────────────────────
function ErrorCard({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = entry.status_code >= 500 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200';
  const codeColor   = entry.status_code >= 500 ? 'text-red-700 bg-red-100' : 'text-yellow-700 bg-yellow-100';

  return (
    <div className={`rounded-xl border p-4 ${statusColor}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${codeColor}`}>
              {entry.status_code}
            </span>
            <span className="text-xs font-mono text-gray-700 truncate">
              {entry.method} {entry.path}
            </span>
            <span className="text-xs text-gray-400">{entry.duration_ms}ms</span>
          </div>
          {entry.error && (
            <p className="text-xs text-gray-700 mt-1.5 font-medium truncate">{entry.error}</p>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-[11px] text-gray-400 font-mono">
              {entry.correlation_id || '—'}
            </span>
            {entry.user_id && (
              <span className="text-[11px] text-gray-400">user #{entry.user_id}</span>
            )}
            <span className="text-[11px] text-gray-400">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}</span>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-gray-400 hover:text-gray-600 flex-shrink-0"
        >
          {expanded ? 'Less ▲' : 'More ▼'}
        </button>
      </div>
      {expanded && (
        <pre className="mt-3 text-[11px] bg-white rounded-lg p-3 overflow-x-auto text-gray-600 border border-gray-200">
          {JSON.stringify(entry, null, 2)}
        </pre>
      )}
    </div>
  );
}